import React, { useState, useEffect, useCallback } from 'react';
import GameHUD from '../shared/GameHUD';
import PauseMenu from '../shared/PauseMenu';
import GameComplete from '../GameComplete';
import { shuffleArray, calculateRewards } from '../../../services/gameService';
import './MemoryGridGame.css';

function MemoryGridGame({
    gameContent,
    template,
    topic,
    difficulty = 'intermediate',
    onComplete,
    onQuit
}) {
    // Game state
    const [cards, setCards] = useState([]);
    const [flippedCards, setFlippedCards] = useState([]);
    const [matchedPairs, setMatchedPairs] = useState([]);
    const [score, setScore] = useState(0);
    const [moves, setMoves] = useState(0);
    const [combo, setCombo] = useState(0);
    const [bestCombo, setBestCombo] = useState(0);
    const [timeLeft, setTimeLeft] = useState(180);
    const [xpEarned, setXpEarned] = useState(0);
    const [coinsEarned, setCoinsEarned] = useState(0);

    // UI state
    const [isPaused, setIsPaused] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const [victory, setVictory] = useState(false);
    const [showConnection, setShowConnection] = useState(null);
    const [isLocked, setIsLocked] = useState(false);

    const initializeCards = useCallback(() => {
        const allCards = [];

        gameContent.pairs.forEach((pair, pairIndex) => {
            // Card 1 is the term
            allCards.push({
                id: `${pair.pairId}-a`,
                pairId: pair.pairId,
                content: pair.card1.content,
                type: pair.card1.type || 'term',
                connection: pair.connection,
                isFlipped: false,
                isMatched: false
            });
            // Card 2 is the definition
            allCards.push({
                id: `${pair.pairId}-b`,
                pairId: pair.pairId,
                content: pair.card2.content,
                type: pair.card2.type || 'definition',
                connection: pair.connection,
                isFlipped: false,
                isMatched: false
            });
        });

        setCards(shuffleArray(allCards));
    }, [gameContent]);

    // Initialize game
    useEffect(() => {
        if (gameContent?.pairs) {
            initializeCards();

            // Set difficulty-based settings
            const diffSettings = template?.difficultyScaling?.[difficulty] || {};
            setTimeLeft(diffSettings.timeLimit || 180);
        }
    }, [gameContent, template, difficulty, initializeCards]);

    const handleGameOver = useCallback((won) => {
        setVictory(won);
        setIsComplete(true);

        const totalPairs = cards.length / 2;
        const accuracy = totalPairs > 0
            ? Math.round((matchedPairs.length / totalPairs) * 100)
            : 0;

        const efficiency = moves > 0 ? Math.round((matchedPairs.length / moves) * 100) : 0;
        const timeBonus = won ? Math.floor(timeLeft / 10) * 5 : 0;

        const rewards = calculateRewards(template, matchedPairs.length, accuracy, timeBonus);

        setXpEarned(rewards.xp);
        setCoinsEarned(rewards.coins);

        if (onComplete) {
            onComplete({
                victory: won,
                score,
                xp: rewards.xp,
                coins: rewards.coins,
                stars: rewards.stars,
                accuracy,
                stats: {
                    matches: matchedPairs.length,
                    moves,
                    efficiency,
                    bestCombo
                }
            });
        }
    }, [score, matchedPairs, cards.length, moves, timeLeft, bestCombo, template, onComplete]);

    // Timer
    useEffect(() => {
        if (isPaused || isComplete || showConnection) return;

        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    handleGameOver(false);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [isPaused, isComplete, showConnection, handleGameOver]);

    // Check for game completion
    useEffect(() => {
        if (cards.length > 0 && matchedPairs.length === cards.length / 2) {
            setTimeout(() => handleGameOver(true), 500);
        }
    }, [matchedPairs, cards.length, handleGameOver]);

    const handleCardClick = (clickedCard) => {
        if (
            isPaused ||
            isComplete ||
            isLocked ||
            clickedCard.isMatched ||
            flippedCards.includes(clickedCard.id) ||
            flippedCards.length >= 2
        ) return;

        const newFlipped = [...flippedCards, clickedCard.id];
        setFlippedCards(newFlipped);

        if (newFlipped.length === 2) {
            setMoves(prev => prev + 1);
            setIsLocked(true);

            const [firstId, secondId] = newFlipped;
            const firstCard = cards.find(c => c.id === firstId);
            const secondCard = cards.find(c => c.id === secondId);

            if (firstCard.pairId === secondCard.pairId) {
                // Match found!
                setTimeout(() => {
                    setCards(prev => prev.map(card =>
                        card.pairId === firstCard.pairId
                            ? { ...card, isMatched: true }
                            : card
                    ));

                    setMatchedPairs(prev => [...prev, firstCard.pairId]);
                    setScore(prev => prev + 100 + (combo * 20));
                    setCombo(prev => {
                        const newCombo = prev + 1;
                        if (newCombo > bestCombo) setBestCombo(newCombo);
                        return newCombo;
                    });
                    setXpEarned(prev => prev + 10);

                    // Show connection message
                    setShowConnection({
                        emoji: firstCard.emoji,
                        text: firstCard.connection
                    });

                    setTimeout(() => {
                        setShowConnection(null);
                        setFlippedCards([]);
                        setIsLocked(false);
                    }, 2000);
                }, 600);
            } else {
                // No match
                setTimeout(() => {
                    setFlippedCards([]);
                    setCombo(0);
                    setIsLocked(false);
                }, 1000);
            }
        }
    };

    const handleRestart = () => {
        initializeCards();
        setFlippedCards([]);
        setMatchedPairs([]);
        setScore(0);
        setMoves(0);
        setCombo(0);
        setBestCombo(0);
        setTimeLeft(template?.difficultyScaling?.[difficulty]?.timeLimit || 180);
        setXpEarned(0);
        setCoinsEarned(0);
        setIsPaused(false);
        setIsComplete(false);
        setVictory(false);
        setShowConnection(null);
        setIsLocked(false);
    };

    const getCardClass = (card) => {
        let classes = ['memory-card'];

        if (card.isMatched) classes.push('matched');
        if (flippedCards.includes(card.id)) classes.push('flipped');

        return classes.join(' ');
    };

    if (!gameContent) {
        return (
            <div className="memory-loading">
                <div className="loading-spinner">🧠</div>
                <p>Loading memory game...</p>
            </div>
        );
    }

    const totalPairs = cards.length / 2;

    return (
        <div className="memory-game">
            <GameHUD
                gameName="Memory Grid"
                score={score}
                timeLeft={timeLeft}
                combo={combo}
                xp={xpEarned}
                coins={coinsEarned}
                onPause={() => setIsPaused(true)}
                showLives={false}
            />

            <div className="memory-info">
                <span className="memory-topic">📚 {topic}</span>
                <div className="memory-stats">
                    <span className="stat">✨ {matchedPairs.length}/{totalPairs} pairs</span>
                    <span className="stat">👆 {moves} moves</span>
                </div>
            </div>

            {/* Connection Toast */}
            {showConnection && (
                <div className="connection-toast">
                    <span className="connection-emoji">✨</span>
                    <p className="connection-text">{showConnection.text}</p>
                </div>
            )}

            <div className="memory-board">
                <div className={`cards-grid cards-${cards.length}`}>
                    {cards.map((card) => (
                        <div
                            key={card.id}
                            className={`${getCardClass(card)} ${card.type === 'term' ? 'term-card' : 'definition-card'}`}
                            onClick={() => handleCardClick(card)}
                        >
                            <div className="card-inner">
                                <div className="card-front">
                                    <span className="card-back-icon">❓</span>
                                </div>
                                <div className="card-back">
                                    <span className="card-type-label">
                                        {card.type === 'term' ? '💡 Term' : '📖 Definition'}
                                    </span>
                                    <span className="card-content">{card.content}</span>
                                </div>
                            </div>
                            {card.isMatched && (
                                <div className="card-matched-overlay">
                                    <span>✓</span>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div className="memory-hint">
                🧠 Match each term with its definition!
            </div>

            {/* Pause Menu */}
            {isPaused && (
                <PauseMenu
                    gameName="Memory Grid"
                    score={score}
                    stats={{
                        matches: matchedPairs.length,
                        combo: bestCombo,
                        timeElapsed: (template?.difficultyScaling?.[difficulty]?.timeLimit || 180) - timeLeft
                    }}
                    onResume={() => setIsPaused(false)}
                    onRestart={handleRestart}
                    onQuit={onQuit}
                />
            )}

            {/* Game Complete */}
            {isComplete && (
                <GameComplete
                    victory={victory}
                    gameName="Memory Grid"
                    score={score}
                    xpEarned={xpEarned}
                    coinsEarned={coinsEarned}
                    stars={victory ? (moves <= totalPairs * 1.5 ? 3 : moves <= totalPairs * 2 ? 2 : 1) : 0}
                    accuracy={totalPairs > 0 ? Math.round((matchedPairs.length / totalPairs) * 100) : 0}
                    stats={{
                        matches: matchedPairs.length,
                        moves,
                        bestCombo
                    }}
                    onPlayAgain={handleRestart}
                    onBackToMap={onQuit}
                    topic={topic}
                />
            )}
        </div>
    );
}

export default MemoryGridGame;
